import SignupHeader from "@/components/pages/signup/signupHeader";
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSlot,
} from "@/components/ui/input-otp";
import PhoneKeyboardBox from "@/components/ui/phoneKeyboardBox";
import Spinner from "@/components/ui/spinner";
import SubmitButton from "@/components/ui/submit-button";
import { cn } from "@/lib/utils";
import { useEffect, useRef } from "react";
import { MutationStatus } from "@tanstack/react-query";

export interface EnterPhoneCodeProps {
	submitMutationStatus: MutationStatus;
	resendMutationStatus: MutationStatus;
	phone: string;
	code: string;
	setCode: (code: string) => void;
	onSubmit: (code: number) => void;
	onResendCode: () => void;
}

export interface EnterPhoneCodeRef {
	reset: () => void;
}

const EnterPhoneCode = ({
	phone,
	onSubmit,
	onResendCode,
	submitMutationStatus,
	resendMutationStatus,
	code,
	setCode,
}: EnterPhoneCodeProps) => {
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		// Focus the input when component mounts
		inputRef.current?.focus();
	}, []);

	const handleKeyPress = (digit: string) => {
		if (code.length < 6) {
			const updatedCode = code + digit;
			setCode(updatedCode);
			if (updatedCode.length === 6) submit(updatedCode);
		}
	};

	const handleBackspace = () => {
		setCode(code.slice(0, -1));
	};

	const handleReset = () => setCode("");

	const submit = (code: string) => {
		const digitsAsNumber = Number(code);
		onSubmit(digitsAsNumber);
	};

	return (
		<div className="flex flex-col items-center justify-between px-6 py-8 bg-white text-textDark h-[100svh] ">
			{/* Back icon */}
			<div className="flex flex-col items-center justify-center w-full">
				{/* Progress bar */}
				<SignupHeader maxProgress={30} />

				{/* Title and description */}
				<div className="mt-4 text-center">
					<h1 className="mb-2 text-2xl font-bold">Enter 6-digit code</h1>
					<p className="text-sm text-textDark">
						{`We've sent the code to ****${phone.slice(-3) ?? 0}`}
					</p>
				</div>
			</div>
			<div>
				{/* Code Input Display */}
				<div className="flex gap-3">
					<InputOTP
						maxLength={6}
						value={code}
						data-testid="input-otp"
						disabled={submitMutationStatus === "pending"}
						onChange={(value) => {
							setCode(value);
							if (value.length === 6) submit(value);
						}}
						ref={inputRef}
					>
						<InputOTPGroup>
							<InputOTPSlot
								className="w-12 h-12 border rounded-2xl flex items-center justify-center text-lg font-medium data-[state=active]:border-primary-600 data-[state=inactive]:border-muted-foreground"
								index={0}
							/>
							<InputOTPSlot
								className="w-12 h-12 border rounded-2xl flex items-center justify-center text-lg font-medium data-[state=active]:border-primary-600 data-[state=inactive]:border-muted-foreground"
								index={1}
							/>
							<InputOTPSlot
								className="w-12 h-12 border rounded-2xl flex items-center justify-center text-lg font-medium data-[state=active]:border-primary-600 data-[state=inactive]:border-muted-foreground"
								index={2}
							/>
							<InputOTPSlot
								className="w-12 h-12 border rounded-2xl flex items-center justify-center text-lg font-medium data-[state=active]:border-primary-600 data-[state=inactive]:border-muted-foreground"
								index={3}
							/>
							<InputOTPSlot
								className="w-12 h-12 border rounded-2xl flex items-center justify-center text-lg font-medium data-[state=active]:border-primary-600 data-[state=inactive]:border-muted-foreground"
								index={4}
							/>
							<InputOTPSlot
								className="w-12 h-12 border rounded-2xl flex items-center justify-center text-lg font-medium data-[state=active]:border-primary-600 data-[state=inactive]:border-muted-foreground"
								index={5}
							/>
						</InputOTPGroup>
					</InputOTP>
				</div>

				{/* Resend */}
				<form
					className="flex justify-center"
					onSubmit={(event) => {
						event.preventDefault();
						onResendCode();
					}}
				>
					<SubmitButton
						mutationStatus={resendMutationStatus}
						className="mt-2"
						variant={"ghost"}
					>
						Resend
					</SubmitButton>
				</form>
				<div
					className={cn(
						"text-center mt-4 flex-col invisible items-center hidden sm:flex",
						{
							visible: submitMutationStatus === "pending",
						},
					)}
				>
					<Spinner className="w-6 fill-primary-600 text-gray-200" />
					<span className="font-medium">Validating code</span>
				</div>
			</div>
			{/* Number pad */}
			<PhoneKeyboardBox
				handleKeyPress={handleKeyPress}
				handleBackspace={handleBackspace}
				handleReset={handleReset}
				className=" pt-[3rem]"
			/>
		</div>
	);
};

export default EnterPhoneCode;
